import axios from "axios";

/**
 * Get or create a session ID for the current browser tab.
 * Stored in sessionStorage so it survives page refreshes but clears on tab close.
 */
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem("vegify_session_id");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("vegify_session_id", sessionId);
  }
  return sessionId;
};

/**
 * Normalize document.referrer to a bare host ("google.com", "instagram.com")
 * for "where did traffic come from" grouping, or null when there's no usable
 * external referrer. Self-referrals — our own host, e.g. an in-tab reload or a
 * back-nav after the browser reset the referrer — are dropped so we never
 * report vegifyit itself as a traffic source.
 */
const getReferrerHost = () => {
  if (!document.referrer) return null;
  try {
    const host = new URL(document.referrer).hostname.replace(/^www\./, "");
    const self = window.location.hostname.replace(/^www\./, "");
    return host === self ? null : host;
  } catch {
    return null;
  }
};

/**
 * Build the one-time acquisition payload for this browser-tab session:
 * { referrer, utmSource? }. Acquisition answers "where did this visit come
 * from", so it's recorded exactly once — on the landing page view — and never
 * on later "what did they do" events (clicks, searches). Returns the payload on
 * the first call only; null afterwards (guarded by a sessionStorage flag), so
 * the same session is never counted twice.
 *
 * `referrer` is the external referring host or the literal "direct" when there
 * is none: that sentinel keeps direct visits countable and marks the record as
 * a landing for the traffic-sources aggregation. `utmSource` is the landing
 * URL's ?utm_source= (campaign tag), included only when present.
 */
export const consumeLandingAcquisition = () => {
  if (sessionStorage.getItem("vegify_acquisition_logged")) return null;
  sessionStorage.setItem("vegify_acquisition_logged", "1");

  const utmSource =
    new URLSearchParams(window.location.search).get("utm_source") || null;

  return {
    referrer: getReferrerHost() || "direct",
    ...(utmSource && { utmSource }),
  };
};

/**
 * Install the session ID as a default axios header so server-initiated
 * analytics logging (e.g. the restaurant search endpoint's logSearchQuery
 * call) can attribute records to the same browser session as client-side
 * logAnalytics calls. Depends on sessionStorage — must not run during SSR.
 */
export const installSessionHeader = () => {
  axios.defaults.headers.common["x-vegify-session"] = getSessionId();
};

/**
 * Derive the analytics source string from a restaurant object.
 * Returns "discovery" for ad hoc search results, null otherwise.
 */
export const getAnalyticsSource = (restaurant) =>
  restaurant?.metadata?.adHocSearch ? "discovery" : null;

/**
 * Log analytics metric to the backend
 * @param {string} metricType - Type of metric (page_view, restaurant_click, search_query)
 * @param {Object} data - Metric-specific data
 * @param {Object} eventContext - Optional event context object with event property
 */
export const logAnalytics = async (
  metricType,
  data = {},
  eventContext = null
) => {
  try {
    const pagePath = window.location.pathname + window.location.search;

    const payload = {
      metricType,
      pagePath,
      sessionId: getSessionId(),
      ...data,
    };

    // Acquisition (referrer + utm_source) is recorded once per session, on the
    // landing page view only — see consumeLandingAcquisition. We don't tag every
    // event: the dashboard tracks where traffic comes from, not what each origin
    // then did.
    if (metricType === "page_view") {
      Object.assign(payload, consumeLandingAcquisition() || {});
    }

    // Add event context if available
    if (eventContext?.event) {
      if (eventContext.event._id) {
        payload.eventId = eventContext.event._id;
      }
      if (eventContext.event.slug) {
        payload.eventSlug = eventContext.event.slug;
      }
    }

    // Fire and forget - don't block UI on analytics
    axios.post("/api/analytics", payload).catch((error) => {
      // Silently fail - analytics shouldn't break the app
      console.error("Analytics logging failed:", error);
    });
  } catch (error) {
    // Silently fail - analytics shouldn't break the app
    console.error("Analytics logging error:", error);
  }
};

/**
 * Log a page view
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logPageView = (eventContext = null) => {
  logAnalytics("page_view", {}, eventContext);
};

/**
 * Log a restaurant click (initial click on restaurant entry)
 * @param {string} restaurantId - Restaurant ID
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logRestaurantClick = (restaurantId, eventContext = null, source = null) => {
  logAnalytics("restaurant_click", { restaurantId, ...(source && { source }) }, eventContext);
};

/**
 * Log a restaurant details click (clicking "see details" link)
 * @param {string} restaurantId - Restaurant ID
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logRestaurantDetailsClick = (
  restaurantId,
  eventContext = null,
  source = null
) => {
  logAnalytics("restaurant_details_click", { restaurantId, ...(source && { source }) }, eventContext);
};

/**
 * Log scrolling through restaurant options (carousel navigation)
 * @param {string} restaurantId - Restaurant ID
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logRestaurantOptionsScroll = (
  restaurantId,
  eventContext = null,
  source = null
) => {
  logAnalytics("restaurant_options_scroll", { restaurantId, ...(source && { source }) }, eventContext);
};

/**
 * Log a website click from restaurant details
 * @param {string} restaurantId - Restaurant ID
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logRestaurantWebsiteClick = (
  restaurantId,
  eventContext = null,
  source = null
) => {
  logAnalytics("restaurant_website_click", { restaurantId, ...(source && { source }) }, eventContext);
};

/**
 * Log a directions click from restaurant details
 * @param {string} restaurantId - Restaurant ID
 * @param {Object} eventContext - Optional event context from useEvent hook
 */
export const logRestaurantDirectionsClick = (
  restaurantId,
  eventContext = null,
  source = null
) => {
  logAnalytics("restaurant_directions_click", { restaurantId, ...(source && { source }) }, eventContext);
};

